import Blob from "cross-blob";
import {
  Document,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";

import {
  CareerRole,
  FieldText,
} from "@/components/ProfileExperience/lib/getCompanyFields";

import { docStyles, renderDateRange } from "../common";
import { numberingStyle } from "../common/numbering";
import { DocGenerator } from "../../index";
import { renderProseMirrorSchemaToGoogleDoc } from "../../proseMirror";
import { ApplicantCompanyFull } from "../../../prisma";
import { parseProseMirrorSchema } from "../../../proseMirror";

interface EmploymentDocArgs {
  applicantCompany: ApplicantCompanyFull;
  briefDescriptionText: FieldText;
  howJoinedText: FieldText;
  projectAimText: FieldText;
  importanceToCompanyText: FieldText;
  whatDidYouDoText: FieldText;
  unfairAdvantageText: FieldText;
  challengingText: FieldText;
  featuredInMediaText: FieldText;
  metricsText: FieldText;
  successAffectText: FieldText;
  whoWasItWithText: FieldText;
  anythingElseYouDidText: FieldText;
  futureThingsToDoText: FieldText;
  leadershipText: FieldText;
  unusualApproachesText: FieldText;
  productFeaturesText: FieldText;
}

export const createEmploymentDoc: DocGenerator<EmploymentDocArgs> = async (
  t,
  {
    application,
    applicantCompany,
    briefDescriptionText,
    howJoinedText,
    projectAimText,
    importanceToCompanyText,
    whatDidYouDoText,
    unfairAdvantageText,
    challengingText,
    featuredInMediaText,
    metricsText,
    successAffectText,
    whoWasItWithText,
    anythingElseYouDidText,
    futureThingsToDoText,
    leadershipText,
    unusualApproachesText,
    productFeaturesText,
  }
) => {
  const careerRoleId = applicantCompany.careerRole?.id;

  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            children: [
              new TextRun({
                text: "Applicant: ",
              }),
              new TextRun({
                text: application.applicant.fullName
                  ? application.applicant.fullName
                  : `${application.applicant.firstName} ${application.applicant.lastName}`,
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Employment at ",
              }),
              new TextRun({
                text: parseProseMirrorSchema(applicantCompany.name),
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          new Paragraph({
            style: "wellSpaced",
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Worked at ",
              }),
              ...renderDateRange(
                applicantCompany.dateFrom,
                applicantCompany.dateTo,
                applicantCompany.workingNow
              ),
            ],
          }),

          new Paragraph({
            style: "wellSpaced",
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Role in the company: ",
                bold: true,
              }),
              new TextRun({
                text: applicantCompany?.careerRole?.name,
              }),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: `${briefDescriptionText.heading}: `,
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            applicantCompany.briefDescription,
            "-"
          ),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: `${howJoinedText.heading}: `,
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(applicantCompany.howJoined, "-"),

          ...applicantCompany.applicantProjectList
            .map((x): any[] => {
              return [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Project: ",
                      bold: true,
                    }),
                    new TextRun({
                      text: parseProseMirrorSchema(x.name),
                    }),
                  ],
                  heading: HeadingLevel.HEADING_1,
                }),
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "What was your role/position in the project?: ",
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.projectPosition, "-"),

                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${projectAimText.heading}: `,
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.projectAim, "-"),

                new Paragraph({}),

                careerRoleId === CareerRole.Marketer
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${productFeaturesText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(
                        x.productFeatures,
                        "-"
                      ),
                    ]
                  : [],

                new Paragraph({}),
                careerRoleId === CareerRole.Investor
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${whoWasItWithText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(x.whoWasItWith, "-"),
                    ]
                  : [],
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${importanceToCompanyText.heading}: `,
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.importanceToCompany, "-"),

                new Paragraph({}),
                careerRoleId !== CareerRole.Investor || !!x.whatDidYouDo
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${whatDidYouDoText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),

                      renderProseMirrorSchemaToGoogleDoc(x.whatDidYouDo, "-"),
                    ]
                  : [],
                new Paragraph({}),
                careerRoleId === CareerRole.Marketer ||
                careerRoleId === CareerRole.BizDev ||
                careerRoleId === CareerRole.SolutionSalesSpecialist ||
                !!x.leadership
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${leadershipText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(x.leadership, "-"),
                    ]
                  : [],
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${unfairAdvantageText.heading}: `,
                      bold: true,
                    }),
                  ],
                }),
                renderProseMirrorSchemaToGoogleDoc(x.unfairAdvantage, "-"),

                new Paragraph({}),
                careerRoleId === CareerRole.Investor || !!x.anythingElseYouDid
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${anythingElseYouDidText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(
                        x.anythingElseYouDid,
                        "-"
                      ),
                    ]
                  : [],
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${challengingText.heading}: `,
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.challenging, "-"),

                new Paragraph({}),
                careerRoleId === CareerRole.Marketer || !!x.unusualApproaches
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${unusualApproachesText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(
                        x.unusualApproaches,
                        "-"
                      ),
                    ]
                  : [],

                new Paragraph({}),
                careerRoleId === CareerRole.Investor
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${futureThingsToDoText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(
                        x.futureThingsToDo,
                        "-"
                      ),
                    ]
                  : [],
                new Paragraph({}),
                careerRoleId === CareerRole.Designer ||
                careerRoleId === CareerRole.Investor
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${successAffectText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(x.successAffect, "-"),
                    ]
                  : [],
                new Paragraph({}),
                careerRoleId !== CareerRole.Investor
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: `${metricsText.heading}: `,
                            bold: true,
                          }),
                        ],
                      }),
                      renderProseMirrorSchemaToGoogleDoc(x.metrics, "-"),
                    ]
                  : [],
                new Paragraph({}),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `${featuredInMediaText.heading}: `,
                      bold: true,
                    }),
                  ],
                }),

                renderProseMirrorSchemaToGoogleDoc(x.featuredInMedia, "-"),
              ];
            })
            .reduce((prev, curr) => prev.concat(curr), []),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  return new Blob([await Packer.toBuffer(document)]);
};
